/**
 * Live_Fused service control panel (mockup "FUSED SURVEILLANCE ADMIN PANEL",
 * `#surv-fused-config` / `#surv-fused-running`).
 *
 * Tick two or more reporting services, start the fusion, and Live_Fused appears
 * as a service in its own right — in the list and in the topbar chip row —
 * until it is stopped.
 *
 * Admin only. State is client-side for now; the backend takes it over later.
 * The starting/stopping delays live here rather than in the store so their
 * timers are cancelled when the panel unmounts.
 */
import { Badge } from '@/components/ui/badge';
import { can } from '@/lib/auth/access';
import { SERVICE_LABEL, type ServiceId } from '@/lib/mqtt/schemas/surveillance';
import { useCurrentRole } from '@/lib/store/auth';
import { selectFusableServices, useSurvStore } from '@/lib/store/surv';
import { cn } from '@/lib/utils';
import { Play, RotateCw, Square, Wifi } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useShallow } from 'zustand/shallow';

const STARTING_MS = 1500;
const STOPPING_MS = 1200;
const RESTART_MS = 1200;
const MIN_SERVICES = 2;

function formatDuration(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

export function FusedServicePanel() {
  const role = useCurrentRole();
  const fusable = useSurvStore(useShallow(selectFusableServices));
  const selected = useSurvStore(useShallow((s) => s.fusedSelected));
  const status = useSurvStore((s) => s.fusedStatus);
  const startedAt = useSurvStore((s) => s.fusedStartedAt);
  const fusedService = useSurvStore((s) => s.fusedService);
  const toggle = useSurvStore((s) => s.toggleFusedService);
  const beginFusing = useSurvStore((s) => s.beginFusing);
  const confirmFusing = useSurvStore((s) => s.confirmFusing);
  const beginStop = useSurvStore((s) => s.beginStopFusing);
  const confirmStop = useSurvStore((s) => s.confirmStopFusing);
  const restart = useSurvStore((s) => s.restartFusing);

  const [restarting, setRestarting] = useState(false);
  const [, tick] = useState(0);

  // Settle the transient states. Cleanup matters: unmounting mid-transition
  // (a context switch) would otherwise fire into a stale panel.
  useEffect(() => {
    if (status === 'starting') {
      const t = setTimeout(confirmFusing, STARTING_MS);
      return () => clearTimeout(t);
    }
    if (status === 'stopping') {
      const t = setTimeout(confirmStop, STOPPING_MS);
      return () => clearTimeout(t);
    }
    return;
  }, [status, confirmFusing, confirmStop]);

  // Re-render once a second so the duration readout advances.
  useEffect(() => {
    if (status !== 'running') return;
    const id = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [status]);

  if (!can('service:fuse', role)) return null;

  const onRestart = () => {
    setRestarting(true);
    setTimeout(() => {
      restart();
      setRestarting(false);
    }, RESTART_MS);
  };

  const count = selected.length;

  return (
    <section className="mt-3.5 border-t border-(--color-border) pt-3">
      <div className="mb-1 flex items-center justify-between">
        <h3 className="text-[0.769em] font-bold uppercase tracking-wider text-(--color-text3)">
          Live_Fused Service
        </h3>
        <span className="rounded bg-(--color-green-bg) px-1.5 py-[2px] text-[0.615em] font-bold uppercase text-(--color-green)">
          Admin
        </span>
      </div>
      <p className="mb-2.5 text-[0.692em] leading-snug text-(--color-text3)">
        Configure and run the Live_Fused service — combines selected surveillance streams into a
        single fused output feed.
      </p>

      {status === 'idle' ? (
        <>
          <div className="mb-1.5 text-[0.615em] font-bold uppercase tracking-wider text-(--color-text3)">
            Select services to fuse
          </div>
          {fusable.length === 0 ? (
            <div className="py-3 text-center text-[0.769em] text-(--color-text3)">
              No services reporting yet.
            </div>
          ) : (
            <div className="space-y-1">
              {fusable.map((svc) => {
                const on = selected.includes(svc.service_id);
                return (
                  <label
                    key={svc.service_id}
                    className={cn(
                      'flex cursor-pointer items-center gap-[7px] rounded-md border px-[7px] py-[5px] transition-colors',
                      on
                        ? 'border-(--color-green)/25 bg-(--color-green-bg)'
                        : 'border-(--color-border) bg-(--color-surface) hover:border-(--color-green)',
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => toggle(svc.service_id)}
                      className="size-[13px] shrink-0 accent-(--color-green)"
                    />
                    <span className="font-mono text-[0.769em]">{svc.label}</span>
                  </label>
                );
              })}
            </div>
          )}
          <button
            type="button"
            disabled={count < MIN_SERVICES}
            onClick={beginFusing}
            className={cn(
              'mt-2 flex w-full items-center justify-center gap-1.5 rounded-md py-[7px] text-[0.846em] font-bold text-white transition-colors',
              count >= MIN_SERVICES
                ? 'cursor-pointer bg-(--color-green) hover:opacity-90'
                : 'cursor-not-allowed bg-(--color-text3) opacity-55',
            )}
          >
            <Play size={12} fill="currentColor" />
            Start Fusing ({count} service{count === 1 ? '' : 's'})
          </button>
          {count < MIN_SERVICES && (
            <div className="mt-1.5 text-center text-[0.615em] text-(--color-text3)">
              Select at least {MIN_SERVICES} services to fuse
            </div>
          )}
        </>
      ) : (
        <>
          <div className="mb-2 grid grid-cols-2 gap-1.5">
            <StatTile
              label="Status"
              value={
                status === 'starting'
                  ? 'Starting…'
                  : status === 'stopping'
                    ? 'Stopping…'
                    : 'Running'
              }
              tone={status === 'running' ? 'ok' : status === 'stopping' ? 'warn' : 'info'}
            />
            <StatTile
              label="Duration"
              value={startedAt == null ? '—' : formatDuration(Date.now() - startedAt)}
              tone="plain"
            />
          </div>

          <div className="mb-2 flex items-center justify-between rounded-md border border-(--color-green)/25 bg-(--color-green-bg) px-2.5 py-[7px]">
            <div className="flex items-center gap-1.5">
              <Wifi size={13} className="text-(--color-green)" />
              <span className="text-[0.769em] font-semibold">Output Stream</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="font-mono text-[0.692em] text-(--color-green)">
                {fusedService ? `${fusedService.visibleTargets.length} targets` : '—'}
              </span>
              <Badge variant="ok">Active</Badge>
            </div>
          </div>

          <div className="mb-2">
            <div className="mb-1 text-[0.615em] font-bold uppercase tracking-wider text-(--color-text3)">
              Fusing
            </div>
            <div className="flex flex-wrap gap-1">
              {selected.map((id) => (
                <span
                  key={id}
                  className="rounded-[3px] bg-(--color-border) px-1.5 py-[2px] font-mono text-[0.615em] text-(--color-text2)"
                >
                  {SERVICE_LABEL[id as ServiceId] ?? id}
                </span>
              ))}
            </div>
          </div>

          <div className="flex gap-1.5">
            <CtrlButton
              onClick={onRestart}
              disabled={restarting || status !== 'running'}
              icon={<RotateCw size={11} className={restarting ? 'animate-spin' : undefined} />}
              label={restarting ? 'Restarting…' : 'Restart'}
            />
            <CtrlButton
              onClick={beginStop}
              disabled={status !== 'running'}
              stop
              icon={<Square size={11} fill="currentColor" />}
              label="Stop"
            />
          </div>
        </>
      )}
    </section>
  );
}

function StatTile({
  label,
  value,
  tone,
}: { label: string; value: string; tone: 'ok' | 'warn' | 'info' | 'plain' }) {
  return (
    <div className="rounded-md border border-(--color-border) bg-(--color-bg) px-2.5 py-[7px]">
      <div className="mb-0.5 text-[0.615em] font-bold uppercase tracking-wider text-(--color-text3)">
        {label}
      </div>
      <div
        className={cn(
          'font-mono text-[0.923em] font-bold',
          tone === 'ok' && 'text-(--color-green)',
          tone === 'warn' && 'text-(--color-amber)',
          tone === 'info' && 'text-(--color-blue)',
        )}
      >
        {value}
      </div>
    </div>
  );
}

function CtrlButton({
  onClick,
  disabled,
  icon,
  label,
  stop,
}: {
  onClick: () => void;
  disabled?: boolean;
  icon: React.ReactNode;
  label: string;
  stop?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex flex-1 cursor-pointer items-center justify-center gap-1 rounded-md border py-[5px] text-[0.769em] font-bold transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-50',
        stop
          ? 'border-(--color-red) bg-(--color-red) text-(--color-on-accent) hover:opacity-85'
          : 'border-(--color-border) bg-(--color-surface) text-(--color-text2) hover:border-(--color-amber) hover:text-(--color-amber)',
      )}
    >
      {icon}
      {label}
    </button>
  );
}
